import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Location } from '~commonInfra/utils/Location';

import { LoginUrlsWrapper } from '../../authentication/components/loginSpace/LoginUrls';
import { IError } from '../../httpClient/serverApi/types/IError';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { StartPageService } from '../../userSettings/services/startPage/StartPageService';

function getSiteUrlFragment(urlName: string): string {
  return `/#/site/${urlName}/`;
}

function getSitePathFragment(urlName: string): string {
  return `/site/${urlName ?? ''}`;
}

function getEncodedSitePathFragment(urlName: string): string {
  return `/site/${encodeURIComponent(urlName) ?? ''}`;
}

/**
* Switches the site if the given urlName if it's not the current site's urlName
*
* Use this instead of SessionManagementService.switchSiteIfNeeded in order to give proper
* feedback in the error conditions
*/
function switchIfNeeded(urlName: string, podRedirectPath?: string): Promise<void> {
  if (podRedirectPath === undefined) {
    podRedirectPath = getSiteUrlFragment(urlName);
  }

  return getSessionManagementServiceInstance().switchSiteIfNeeded_DoNotCallDirectly(urlName, podRedirectPath)
    .catch((error: IError) => {
      // This error code is returned when the current user doesn't have access to the site they're trying to switch to.
      // The user must log in using different credentials, specific to the site. For now, it is supported only when
      // third party auth is configured (i.e. SiteSAML is enabled).
      if (error && error.code === CommonErrorCode.SiteUserNotFound && ServerSettingsUnauthenticated.isSiteSamlEnabled()) {
        LoginUrlsWrapper.navigateToSignIn({
          site: urlName
        });
      } else {
        LoginUrlsWrapper.navigateToSignInErrorCodeFromSiteSwitch('' + error.code);
      }
      return Promise.reject(error);
    });
}

function switchIfNeededAndRedirectToStartPage(urlName: string, podRedirectPath?: string): Promise<void> {
  return switchIfNeeded(urlName, podRedirectPath).then(() => {
    Location.instance.setHash(StartPageService.getCurrentUserStartPageForCurrentSite());
  });
}

function switchIfNeededAndRedirectToAuth(urlName: string, podRedirectPath: string): Promise<void> {
  podRedirectPath += '&target_site=' + urlName;
  return switchIfNeeded(urlName, podRedirectPath).then(() => {
    // externalRedirect: does not require hash prefix, this goes out of the # scope.
    WindowLocationService.setPathQueryFragment(podRedirectPath);
  });
}

function switchIfNeededNoFeedback(urlName: string): Promise<void> {
  return getSessionManagementServiceInstance().switchSiteIfNeeded_DoNotCallDirectly(urlName);
}

export const SiteSwitchService = {
  getSiteUrlFragment,
  getSitePathFragment,
  getEncodedSitePathFragment,
  switchIfNeeded,
  switchIfNeededAndRedirectToStartPage,
  switchIfNeededAndRedirectToAuth,
  switchIfNeededNoFeedback
};
